import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { RouterModule, Routes } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { environment } from '../environments/environment';
import { ErrorInterceptor } from './core/error.interceptor';
import { AppErrorHandler } from './app-error-handler';
import { ConfirmationService, MessageService } from 'primeng/api';
import { APP_CONFIG } from './core/config/app-config.service';
import { CommonModule } from '@angular/common';
import { HeaderModule } from './app-components/header/header.module';
import { FooterModule } from './app-components/footer/footer.module';
import { ApiUrlInterceptor } from './core/api/api-url-interceptor';
import { LoaderInterceptor } from './core/loader/loader.interceptor';
import { LoaderModule } from './ui-components/loader/loader.module';
// Import the module from the SDK
import { AuthModule } from '@auth0/auth0-angular';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    `${'.'}/assets/i18n/`,
    '.json'
  );
}

function getOktaRedirectURL(): string{
  let tag = document.querySelector('base');
  if(tag){
    let url = tag.href;
    if(url !== '' && url !== '/'){
      return url;
    }
  }
  return window.location.origin;
}

// read from config
function initializeKeycloak(keycloak: KeycloakService,) {
  if(!(environment as any).keyCloakConfig) return () => {};

  return () =>
    keycloak.init({
      config: {
        url: (environment as any).keyCloakConfig?.url || '',
        realm: (environment as any).keyCloakConfig?.realm || '',
        clientId: (environment as any).keyCloakConfig?.clientId || ''
      },
      initOptions: {
        checkLoginIframe: false
      },
      enableBearerInterceptor: false
    });
}

function initializeAppConfig() {
  return environment
}

const routes: Routes = [{
  path: '',
  loadChildren: () => import('./home-page/home-page.module').then(m => m.HomePageModule)
}, {
  path: 'apartments',
  loadChildren: () => import('./apartment-search-page/apartment-search-page.module').then(m => m.ApartmentSearchPageModule),
}, {
  path: 'apartments/results',
  loadChildren: () => import('./apartment-search-result/apartment-search-result.module').then(m => m.ApartmentSearchResultModule)
}, {
  path: 'applications/addons',
  loadChildren: () => import('./apartment-extra-page/apartment-extra-page.module').then(m => m.ApartmentExtraPageModule)
}, 
{
  path: 'auth0/callback',
  loadChildren: () => import('./auth0-callback/auth0-callback.module').then(m =>m.Auth0CallbackModule)
},
{
  path: 'auth0/callback/logout',
  loadChildren: () => import('./auth0-callback-logout/auth0-callback-logout.module').then(m =>m.Auth0CallbackLogoutModule)
},
// Not found page
{
  path: '**',
  loadChildren: () => import('./not-found-page/not-found-page.module').then(m => m.NotFoundPageModule)
}]

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    // NoopAnimationsModule,
    AppRoutingModule,
    RouterModule.forRoot(routes),
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    KeycloakAngularModule,
    HeaderModule,
    FooterModule,
    LoaderModule,
    ToastModule,
    ConfirmDialogModule,
    AuthModule.forRoot({
      domain: environment.oktaConfig ? environment.oktaConfig.domain : '',
      clientId: environment.oktaConfig ? environment.oktaConfig.clientId: '',
      authorizationParams: {
        redirect_uri: getOktaRedirectURL()
      },
      skipRedirectCallback: window.location.href.indexOf('auth0/callback') !== -1,
    })
  ],
  exports: [
    TranslateModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService]
    },
    {
      provide: APP_CONFIG,
      useFactory: initializeAppConfig
    },
    ConfirmationService,
    MessageService,
    // override error handle from xn-ng-framework
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: ErrorHandler, useClass: AppErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiUrlInterceptor, multi: true },
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          // disable dark mode entirely
          darkModeSelector: false
        }
      }
    }),
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
